// src/App.jsx
import { App as CapacitorApp } from '@capacitor/app';
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth, ROLES, ROLE_LABELS } from './context/AuthContext';
import { planForFeature, PLAN_LABELS } from './config/plans';
import PlanGate from './components/PlanGate';
import { api, getSession } from './services/api'; 

// Pages
import InboundLeads      from './pages/InboundLeads';
import LeadSources       from './pages/LeadSources';
import LeadIntakeReport  from './pages/LeadIntakeReport';
import Login       from './pages/Login';
import Dashboard   from './pages/Dashboard';
import Customers   from './pages/Customers';
import Leads       from './pages/Leads';
import QuotesList  from './pages/QuotesList';
import CompanySettings from './pages/CompanySettings';
import QuoteForm   from './pages/QuoteForm';
import Invoices    from './pages/Invoices';
import Tasks       from './pages/Tasks';
import Expenses    from './pages/Expenses';
import Calls       from './pages/Calls';
import OCRScanner  from './pages/OCRScanner';
import Reports     from './pages/Reports';
import Products    from './pages/Products';
import Users       from './pages/Users';
import Pdf         from './pages/Pdf';
import Followup    from './pages/FollowUps';
import UpgradePlan from './pages/UpgradePlan';


function useSessionResumeCheck() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    let listener;
    const sub = CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive && user) {
        try {
          // Any lightweight authenticated call works as a ping —
          // getPlanUsage is cheap and already exists
          await api('getPlanUsage', {});
        } catch (ex) {
          if (ex.message?.includes('Unauthorized')) {
            clearSession();
            navigate('/login');
          }
        }
      }
    }).then(handle => {
    listener = handle;
  });
    return () => {
      listener?.remove();
    };
  }, [navigate]);
}

// ── Auth guard ────────────────────────────────────────────────
function RequireAuth({ children, roles }) {
  useSessionResumeCheck();
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.ryts)) return <Navigate to="/" replace />;
  return children;
}

// ── Suspended-account guard ─────────────────────────────────────
// A corp can be blocked independently of plan (e.g. non-payment) —
// this is separate from feature gating and blocks the ENTIRE app.
function RequireActiveAccount({ children }) {
  const { user } = useAuth();
  if (user?.accountBlocked) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="card" style={{ maxWidth: 420, textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '.6rem' }}>⛔</div>
          <div style={{ fontWeight: 700, marginBottom: '.5rem' }}>Account Suspended</div>
          <p style={{ color: 'var(--muted)', fontSize: '.9rem' }}>
            {user.accountBlockedReason || 'Your organisation\u2019s account is currently inactive. Please contact support.'}
          </p>
        </div>
      </div>
    );
  }
  return children;
}

// ── Sidebar nav config ────────────────────────────────────────
// `feature` marks a nav item as plan-gated — it still shows (with a
// lock badge) so people can see and click into the upsell, rather
// than being hidden entirely.
const NAV = [
  { to: '/',           icon: '📊', label: 'Dashboard'  },
  { to: '/leads',      icon: '🎯', label: 'Leads'       },
  { to: '/customers',  icon: '👥', label: 'Customers'   },
  { to: '/quotes',     icon: '📋', label: 'Quotes'      },
  { to: '/inbound-leads',      label: 'Inbound Leads',  icon: '📥' },
  { to: '/lead-intake-report', roles: [ROLES.ADMIN, ROLES.MANAGER], label: 'Lead Report',    icon: '📊' },
  { to: '/lead-sources',      roles: [ROLES.ADMIN, ROLES.MANAGER], label: 'Lead Sources',   icon: '🔗' },
  { to: '/tasks',      icon: '✅', label: 'Tasks',  feature: 'tasks'     },
  { to: '/expenses',   icon: '📍', label: 'Visit/Expenses', feature: 'visits_expenses' },
  { to: '/calls',      icon: '📞', label: 'Calls', feature: 'call_records' },
  { to: '/followups',  icon: '🔔', label: 'Follow-ups', feature: 'followups' },
  { to: '/ocr',        icon: '📷', label: 'Card Scan',  feature: 'ocr' },
  { to: '/reports',    icon: '📈', label: 'Reports',    roles: [ROLES.ADMIN, ROLES.MANAGER], feature: 'reports' },
  { to: '/products',   icon: '📦', label: 'Products',   roles: [ROLES.ADMIN] },
  { to: '/users',      icon: '👤', label: 'Users',      roles: [ROLES.ADMIN] },
  { to: '/company',    icon: '🏢', label: 'Company',    roles: [ROLES.ADMIN], feature: 'company_branding_edit' },
];

// ── Sidebar ───────────────────────────────────────────────────
function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visible = NAV.filter(n => !n.roles || n.roles.includes(user?.ryts));

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <>
      {open && <div className="overlay-bg" onClick={onClose} />}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-user">
          <div className="avatar">{user?.name?.[0]?.toUpperCase()}</div>
          <div>
            <div className="sidebar-name">{user?.name}</div>
            <div className="sidebar-role">{ROLE_LABELS[user?.ryts]} · {user?.business}</div>
          </div>
        </div>

        {/* Current plan badge + upgrade shortcut */}
        <div style={{ padding: '.5rem 1rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="badge" style={{ background: 'var(--surface)', fontSize: '.72rem' }}>
            {PLAN_LABELS[user?.plan] || 'Free'} plan
          </span>
          {(user?.plan || 'free') !== 'enterprise' && (
            <NavLink to="/upgrade" onClick={onClose} style={{ fontSize: '.72rem', color: 'var(--accent)' }}>
              Upgrade →
            </NavLink>
          )}
        </div>

        {visible.map(n => {
          const locked = n.feature && !planForFeature(user?.plan, n.feature);
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={onClose}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span><span>{n.icon}</span> {n.label}</span>
              {locked && <span title="Upgrade to unlock" style={{ fontSize: '.75rem' }}>🔒</span>}
            </NavLink>
          );
        })}

        <div className="nav-sep" />
        <button className="nav-link" style={{ border: 'none', background: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: 'var(--red)' }} onClick={handleLogout}>
          ⏻ Logout
        </button>
      </aside>
    </>
  );
}

// ── App shell (authenticated layout) ─────────────────────────
function Shell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    
    <RequireActiveAccount>
      <div className="layout">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="main-wrap">
          {/* Mobile topbar */}
          <header className="topbar">
            <button className="icon-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
            <span className="topbar-brand">BizzFlowSuite</span>
            <span className="topbar-corp">{user?.business}</span>
          </header>

          <div className="page-content">
            <Routes>
              <Route path="/"          element={<Dashboard />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/leads"     element={<Leads />} />
              <Route path="/followups" element={
                <PlanGate feature="followups"><Followup /></PlanGate>
              } />
              <Route path="/quotes"    element={<QuotesList />} />
              <Route path="/quotes/new"element={<QuoteForm />} />
              <Route path="/quotes/:id"element={<QuoteForm />} />
              <Route path="/invoices"  element={
                <RequireAuth roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.FINANCE]}>
                  <Invoices />
                </RequireAuth>
              } />
              <Route path="/inbound-leads" element={
                <PlanGate feature="inbound_leads"><InboundLeads /></PlanGate>
              } />
              <Route path="/lead-sources" element={
                <PlanGate feature="inbound_leads">
                  <RequireAuth roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.FINANCE]}> <LeadSources /> </RequireAuth>
                </PlanGate>
              } />
              <Route path="/lead-intake-report"  element={
                <PlanGate feature="lead_intake_report"><LeadIntakeReport /></PlanGate>
              } />
              <Route path="/tasks"     element={
                <PlanGate feature="tasks"><Tasks /></PlanGate>
              } />
              <Route path="/Pdf/:id"   element={<Pdf />} />
              <Route path="/expenses"  element={
                <PlanGate feature="visits_expenses"><Expenses /></PlanGate>
              } />
              <Route path="/calls"     element={
                <PlanGate feature="call_records"><Calls /></PlanGate>
              } />
              <Route path="/ocr"       element={
                <PlanGate feature="ocr"><OCRScanner /></PlanGate>
              } />
              <Route path="/reports"   element={
                <RequireAuth roles={[ROLES.ADMIN, ROLES.MANAGER]}>
                  <PlanGate feature="reports"><Reports /></PlanGate>
                </RequireAuth>
              } />
              <Route path="/users"   element={
                <RequireAuth roles={[ROLES.ADMIN, ROLES.MANAGER]}>
                  <Users />
                </RequireAuth>
              } />
              <Route path="/company" element={
                <RequireAuth roles={[ROLES.ADMIN, ROLES.MANAGER]}>
                  <PlanGate feature="company_branding_edit"><CompanySettings /></PlanGate>
                </RequireAuth>
              } />
              <Route path="/products"  element={
                <RequireAuth roles={[ROLES.ADMIN]}>
                  <Products />
                </RequireAuth>
              } />
              <Route path="/upgrade"   element={<UpgradePlan />} />
              <Route path="*"          element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </RequireActiveAccount>
  );
}

// ── Root ──────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/*"     element={<RequireAuth><Shell /></RequireAuth>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  return user ? <Navigate to="/" replace /> : children;
}
